import { Column, Entity, Index, Unique } from 'typeorm';
import { BaseEntity } from './base.entity';

/** Who on the account matched (PD-13954). */
export type ClientExclusionMatchSubjectKind =
	| 'CLIENT'
	| 'CLIENT_INDIVIDUAL'
	| 'PERSONNEL';

/** The strongest key the match was made on, strongest first. */
export type ClientExclusionMatchKey = 'NPI' | 'NAME_ADDRESS' | 'NAME_STATE';

/** Why a match stopped holding. */
export type ClientExclusionMatchClearReason =
	| 'REINSTATED'
	| 'REMOVED_FROM_LEIE'
	| 'NO_LONGER_MATCHES';

/**
 * One candidate federal exclusion match (PD-13954): one of this tenant's
 * accounts, an account that is a person, or a person the state boards link to
 * an account (pharmacist-in-charge, staff pharmacist), against one record of
 * the OIG List of Excluded Individuals / Entities (LEIE).
 *
 * WHERE IT LIVES AND WHO WRITES IT. In the tenant's own database, the way the
 * matchmaker keeps `client_license_match` (Chris, 2026-10-07). The public OIG
 * list stays in mission control (`workd_mission_control.lm_source_leie`, which
 * tenant logins read with a table grant, like `lm_registry`); this table holds
 * this tenant's customers, so it never goes there. The licensing screening
 * job writes it monthly, after the LEIE import, as the shared licensing
 * account (`scripts/license-management/leie/screen-tenants.mjs`, raw SQL like
 * the matchmaker's other bulk writes; it needs a per-table SELECT, INSERT,
 * UPDATE grant here). crm-api only reads it, for the Compliance dashboard's
 * Federal Exclusion card and list; the officer's decisions are
 * `client_exclusion_review`, keyed by `<subjectKind>:<subjectId>:<leieId>`, so
 * a decision survives a rebuild of this table.
 *
 * Rows are never deleted. A match that stops holding is CLEARED with the
 * reason (OIG reinstated the party, it left OIG's full file, or our side
 * changed: the account was deleted or its NPI or name corrected), and a
 * cleared match that holds again is reopened, so the table answers "was this
 * account ever a candidate, on which key, and when did that end". The row's
 * `_created_date` is when it was first matched.
 */
@Unique('uq_cem_subject_leie', ['subjectKind', 'subjectId', 'leieId'])
@Index('idx_cem_client', ['fkClientId'], {})
@Index('idx_cem_open', ['clearedAt'], {})
@Entity('client_exclusion_match')
export class ClientExclusionMatchEntity extends BaseEntity {
	@Column('varchar', { name: 'subjectKind', length: 20 })
	public subjectKind!: ClientExclusionMatchSubjectKind;

	/** `client._id`, or `facility_personnel._id` for PERSONNEL. */
	@Column('int', { name: 'subjectId', unsigned: true })
	public subjectId!: number;

	/** The account the subject belongs to (`client._id`). */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** `workd_mission_control.lm_source_leie.id` of the OIG record. */
	@Column('int', { name: 'leieId', unsigned: true })
	public leieId!: number;

	@Column('varchar', { name: 'matchKey', length: 16 })
	public matchKey!: ClientExclusionMatchKey;

	/** `IN_EFFECT`, or `NOT_YET_EFFECTIVE` for an exclusion announced with a later date. */
	@Column('varchar', { name: 'effect', length: 20 })
	public effect!: string;

	/** A non-cancelled order in the 90 days before the screening run. */
	@Column({ type: 'boolean', name: 'isActiveBuyer', width: 1, default: 0 })
	public isActiveBuyer!: boolean;

	/** How the two sides agree: nameAgrees, addressAgrees/addressVia, stateAgrees, npiDerived, credential. */
	@Column('json', { name: 'signals', nullable: true })
	public signals!: Record<string, unknown> | null;

	/** The names, addresses and NPIs the match was made on, as screened. */
	@Column('json', { name: 'subjectSnapshot', nullable: true })
	public subjectSnapshot!: Record<string, unknown> | null;

	@Column('datetime', {
		name: 'lastMatchedAt',
		default: () => 'CURRENT_TIMESTAMP',
	})
	public lastMatchedAt!: Date;

	/** `leie-screen-<UTC timestamp>` of the run that last wrote the row. */
	@Column('varchar', { name: 'lastRunId', length: 40, nullable: true })
	public lastRunId!: string | null;

	@Column('datetime', { name: 'clearedAt', nullable: true })
	public clearedAt!: Date | null;

	@Column('varchar', { name: 'clearReason', length: 24, nullable: true })
	public clearReason!: ClientExclusionMatchClearReason | null;
}
